<mat-card class="detail-card" valumizeCardContextMenu [commentScope]="'fund-report'" [commentSlug]="'fund-partnership-investments'">
    <mat-card-header class="detail-card-header">
        <mat-card-title data-test="title">Partnership Investments</mat-card-title>
    </mat-card-header>
    <mat-card-subtitle>
        @if ((selectedFundInvestmentsForFundReport$ | async); as fundInvestmentsForFundReport) {
            @if (fundInvestmentsForFundReport.status === 'ERROR') {
                <valumize-info-panel [appearance]="'ERROR'"
                                     [message]="fundInvestmentsForFundReport.errorMessage"></valumize-info-panel>
            }
            @if (fundInvestmentsForFundReport.status === 'SAVED') {
                <valumize-info-panel [appearance]="'SAVED'" [message]="'Company successfully added'"></valumize-info-panel>
            }
            @if (fundInvestmentsForFundReport.status === 'LOADING') {
                <mat-progress-spinner></mat-progress-spinner>
            }
        }
        @if (hasNegativeUndrawn$ | async) {
            <valumize-info-panel [appearance]="'WARNING'"
                                 [message]="'Total undrawn is negative — please review fund report drawdowns.'">
            </valumize-info-panel>
        }
        @if ((partnershipInvestments$ | async)?.zeroForecastReviewCount; as reviewCount) {
            <valumize-info-panel data-test="zero-forecast-banner"
                                 [appearance]="'WARNING'"
                                 [message]="reviewCount + (reviewCount === 1 ? ' asset has' : ' assets have') + ' no forecast cashflow. Review each, and confirm whether it is a write-off.'">
            </valumize-info-panel>
        }
    </mat-card-subtitle>
    @if (fundId$ | async; as fundId) {
        @if ((selectedFundReport$ | async)?.fundReport?.data?.id; as fundReportId) {
            @if (fundReportId) {
                <mat-card-content class="detail-card-content">
                    @if (partnershipInvestments$ | async; as partnershipInvestments) {
                        <section class="table-container pi-sticky-scroll" tabindex="0">
                            <table mat-table [dataSource]="partnershipInvestmentDataSource"
                                   matSort #partnershipInvestmentSort="matSort">

                                <!-- ==================== Column definitions (header + data cells) ==================== -->

                                <ng-container matColumnDef="company" sticky>
                                    <th *matHeaderCellDef mat-header-cell mat-sort-header>Company</th>
                                    <td *matCellDef="let element" (click)="routeToAssetDetails(
                                        element.assetId,
                                        fundId,
                                        fundReportId,
                                        fundValuationId!,
                                        element.fundInvestmentId
                                        )" class="no-wrap" mat-cell>
                                        @if (element.isArchived?.value) {
                                            <mat-icon class="archived-icon">inventory_2</mat-icon>
                                        }
                                        @if (needsWriteoffReview(element)) {
                                            <mat-icon class="writeoff-flag-icon"
                                                      data-test="writeoff-review-icon"
                                                      (click)="$event.stopPropagation(); confirmWriteoff(element)"
                                                      matTooltip="No forecast cashflow modelled. Is this asset a write-off?">warning_amber</mat-icon>
                                        }
                                        @if (element.company.source !== 'manually added') {
                                            <a>{{ element.company | traceableFormat }}</a>
                                        } @else {
                                            {{ element.company | traceableFormat }}
                                        }
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="activityIndustry">
                                    <th [ngClass]="getGroupCssClass('activityIndustry')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('activityIndustry')">Activity/Industry
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('activityIndustry')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GENERAL)">
                                        {{ element.activityIndustry | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="country">
                                    <th [ngClass]="getGroupCssClass('country')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('country')">Country
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('country')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GENERAL)">
                                        {{ element.country.code | codeFormat: codeTableCountry | async }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="gic">
                                    <th [ngClass]="getGroupCssClass('gic')" *matHeaderCellDef mat-header-cell mat-sort-header [class.grp-start-data]="groupStartCols.has('gic')">
                                        GIC
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('gic')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GENERAL)">
                                        {{ element.gic.code | codeFormat: codeTableGic | async }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="gpIndication">
                                    <th [ngClass]="getGroupCssClass('gpIndication')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('gpIndication')">GP Indication
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('gpIndication')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GENERAL)">
                                        {{ element.gpIndication.code | codeFormat: codeTableGpIndication | async }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="investmentDate">
                                    <th [ngClass]="getGroupCssClass('investmentDate')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('investmentDate')">Inv. Date
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('investmentDate')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.EXITINFO)">
                                        {{ element.investmentDate | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="percentageHeld">
                                    <th [ngClass]="getGroupCssClass('percentageHeld')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('percentageHeld')">% held
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('percentageHeld')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GENERAL)">
                                        @if (element.percentageHeld && element.percentageHeld.fraction) {
                                            {{ element.percentageHeld | traceableFormat }}
                                        } @else {
                                            <mat-icon matTooltip="Please provide an ownership stake in the &quot;Manage Investments&quot; form." class="small-warning-icon">
                                                warning_amber
                                            </mat-icon>
                                        }
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="remainingCost">
                                    <th [ngClass]="getGroupCssClass('remainingCost')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('remainingCost')">Remaining Cost
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('remainingCost')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.remainingCost | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="gpNAV">
                                    <th [ngClass]="getGroupCssClass('gpNAV')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('gpNAV')">GP NAV
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('gpNAV')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.gpNAV | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="unrealizedTVPI">
                                    <th [ngClass]="getGroupCssClass('unrealizedTVPI')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('unrealizedTVPI')">Unrealized TVPI
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('unrealizedTVPI')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.unrealizedTVPI | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="totalTVPI">
                                    <th [ngClass]="getGroupCssClass('totalTVPI')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('totalTVPI')">Total TVPI
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('totalTVPI')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.totalTVPI | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="exitDateAssumption">
                                    <th [ngClass]="getGroupCssClass('exitDateAssumption')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('exitDateAssumption')">Exit Date <br/> Assumption
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('exitDateAssumption')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.EXITINFO)">
                                        {{ element.exitDateAssumption | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="valuationType">
                                    <th [ngClass]="getGroupCssClass('valuationType')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('valuationType')">Valuation Type
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('valuationType')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ displayValuationType(element.preferredValuationType.code) }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="discountRate">
                                    <th [ngClass]="getGroupCssClass('discountRate')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('discountRate')">Discount Rate
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('discountRate')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.discountRate | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="selectedBid">
                                    <th [ngClass]="getGroupCssClass('selectedBid')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('selectedBid')">Selected Bid
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('selectedBid')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.selectedBid | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="discountToNAV">
                                    <th [ngClass]="getGroupCssClass('discountToNAV')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('discountToNAV')">Discount to NAV
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('discountToNAV')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.discountToNAV | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="percentageOfTotalBid">
                                    <th [ngClass]="getGroupCssClass('percentageOfTotalBid')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('percentageOfTotalBid')">% of total Bid
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('percentageOfTotalBid')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.percentageOfTotalBid | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="percentageOfTotalNAV">
                                    <th [ngClass]="getGroupCssClass('percentageOfTotalNAV')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('percentageOfTotalNAV')">% of total NAV
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('percentageOfTotalNAV')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.percentageOfTotalNAV | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="stabilityOfCFs">
                                    <th [ngClass]="getGroupCssClass('stabilityOfCFs')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('stabilityOfCFs')">Stability of CFs
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('stabilityOfCFs')"
                                        class="{{setBackgroundColor(element.stabilityOfCFs.code)}}"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.stabilityOfCFs.code | codeFormat: codeTableAssessment | async }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="realizedTVPI">
                                    <th [ngClass]="getGroupCssClass('realizedTVPI')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('realizedTVPI')">Realized TVPI
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('realizedTVPI')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.realizedTVPI | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="realizedCost">
                                    <th [ngClass]="getGroupCssClass('realizedCost')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('realizedCost')">Realized Cost
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('realizedCost')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.realizedCost | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="realizedGains">
                                    <th [ngClass]="getGroupCssClass('realizedGains')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('realizedGains')">Realized Gains
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('realizedGains')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.realizedGains | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="totalRealized">
                                    <th [ngClass]="getGroupCssClass('totalRealized')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('totalRealized')">Total Realized
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('totalRealized')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.totalRealized | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="totalCost">
                                    <th [ngClass]="getGroupCssClass('totalCost')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('totalCost')">Total Cost
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('totalCost')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.totalCost | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="mezzanine">
                                    <th [ngClass]="getGroupCssClass('mezzanine')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('mezzanine')">Mezzanine
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('mezzanine')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.mezzanine | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="preferredEquity">
                                    <th [ngClass]="getGroupCssClass('preferredEquity')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('preferredEquity')">Preferred Equity
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('preferredEquity')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.preferredEquity | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="commonEquity">
                                    <th [ngClass]="getGroupCssClass('commonEquity')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('commonEquity')">Common Equity
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('commonEquity')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.commonEquity | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="totalIRR">
                                    <th [ngClass]="getGroupCssClass('totalIRR')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('totalIRR')">Total IRR
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('totalIRR')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.totalIRR | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="realizedPercentageOfInvestment">
                                    <th [ngClass]="getGroupCssClass('realizedPercentageOfInvestment')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('realizedPercentageOfInvestment')">Real. % of Inv.
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('realizedPercentageOfInvestment')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.realizedPercentageOfInvestment | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="committedInvestment">
                                    <th [ngClass]="getGroupCssClass('committedInvestment')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('committedInvestment')">Committed Inv.
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('committedInvestment')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.committedInvestment | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="capexDrawdown">
                                    <th [ngClass]="getGroupCssClass('capexDrawdown')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('capexDrawdown')">Capex Drawdown
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('capexDrawdown')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.capexDrawdown | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="percentageOfProceeds">
                                    <th [ngClass]="getGroupCssClass('percentageOfProceeds')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('percentageOfProceeds')">% Proceeds
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('percentageOfProceeds')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.percentageOfProceeds | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="evOverEbitda">
                                    <th [ngClass]="getGroupCssClass('evOverEbitda')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('evOverEbitda')">EV/EBITDA
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('evOverEbitda')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.PURCHASEMULTIPLES)">
                                        {{ isDiscountMultipleValuation(element) ? 'n.a.' : (element.evOverEbitda | traceableFormat) }}{{ showEarningBaseStar(element) ? ' *' : '' }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="evOverRevenue">
                                    <th [ngClass]="getGroupCssClass('evOverRevenue')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('evOverRevenue')">EV/Revenue
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('evOverRevenue')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.PURCHASEMULTIPLES)">
                                        {{ isDiscountMultipleValuation(element) ? 'n.a.' : (element.evOverRevenue | traceableFormat) }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="impliedTrading">
                                    <th [ngClass]="getGroupCssClass('impliedTrading')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('impliedTrading')">GP-implied<br>EV/EBITDA
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('impliedTrading')">
                                        @if (element.impliedTrading?.gpEvEbitda !== null && element.impliedTrading?.gpEvEbitda !== undefined) {
                                            {{ element.impliedTrading.gpEvEbitda | decimalFormat: "multiple" }}
                                        }
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="ebitdaMargin">
                                    <th [ngClass]="getGroupCssClass('ebitdaMargin')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('ebitdaMargin')">Margin
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('ebitdaMargin')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.GPVALUATION)">
                                        {{ element.ebitdaMargin | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="revenueCAGR">
                                    <th [ngClass]="getGroupCssClass('revenueCAGR')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('revenueCAGR')">Rev CAGR
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('revenueCAGR')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ isDiscountMultipleValuation(element) ? 'n.a.' : (element.revenueCAGR | traceableFormat) }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="marginExpansion">
                                    <th [ngClass]="getGroupCssClass('marginExpansion')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('marginExpansion')">EBITDA Margin exp.
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('marginExpansion')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ isDiscountMultipleValuation(element) ? 'n.a.' : (element.marginExpansion | traceableFormat : true) }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="weightedRemainingHold">
                                    <th [ngClass]="getGroupCssClass('weightedRemainingHold')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('weightedRemainingHold')">Weighted Rem. Hold
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('weightedRemainingHold')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.weightedRemainingHold | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="vsComps">
                                    <th [ngClass]="getGroupCssClass('vsComps')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('vsComps')">vs. Comps
                                        <mat-icon class="info-icon" aria-label="Column info"
                                                  matTooltip="Comparison between asset and its comps across the following metrics:
                                                                   • EV/EBITDA
                                                                   • EV/Revenue
                                                                   • EBITDA margin"
                                                  matTooltipClass="custom-tooltip" (click)="$event.stopPropagation()">info_outline
                                        </mat-icon>
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('vsComps')">
                                        {{ element.vsComps | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="cashflowLow">
                                    <th [ngClass]="getGroupCssClass('cashflowLow')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('cashflowLow')" [class.scenario-start]="isScenarioStart('cashflowLow')">Low Case
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('cashflowLow')"
                                        [class.scenario-start]="isScenarioStart('cashflowLow')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ getDisplayScenarioValue(element.cashflowLow, partnershipInvestments.partnershipInvestment.netCurrentAssets, element.assetId, element.company) | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="navMultipleLow">
                                    <th [ngClass]="getGroupCssClass('navMultipleLow')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('navMultipleLow')" [class.scenario-start]="isScenarioStart('navMultipleLow')">NAV Mult
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('navMultipleLow')"
                                        [class.scenario-start]="isScenarioStart('navMultipleLow')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.navMultipleLow | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="primaryTVPILow">
                                    <th [ngClass]="getGroupCssClass('primaryTVPILow')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('primaryTVPILow')" [class.scenario-start]="isScenarioStart('primaryTVPILow')">Prim. TVPI
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('primaryTVPILow')"
                                        [class.scenario-start]="isScenarioStart('primaryTVPILow')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.primaryTVPILow | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="secondaryTVPILow">
                                    <th [ngClass]="getGroupCssClass('secondaryTVPILow')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('secondaryTVPILow')" [class.scenario-start]="isScenarioStart('secondaryTVPILow')">Sec. TVPI
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('secondaryTVPILow')"
                                        [class.scenario-start]="isScenarioStart('secondaryTVPILow')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.secondaryTVPILow | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="secondaryIRRLow">
                                    <th [ngClass]="getGroupCssClass('secondaryIRRLow')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('secondaryIRRLow')" [class.scenario-start]="isScenarioStart('secondaryIRRLow')">Sec. IRR
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('secondaryIRRLow')"
                                        [class.scenario-start]="isScenarioStart('secondaryIRRLow')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.secondaryIRRLow | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="cashflowBase">
                                    <th [ngClass]="getGroupCssClass('cashflowBase')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('cashflowBase')" [class.scenario-start]="isScenarioStart('cashflowBase')">Base Case
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('cashflowBase')"
                                        [class.scenario-start]="isScenarioStart('cashflowBase')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ getDisplayScenarioValue(element.cashflowBase, partnershipInvestments.partnershipInvestment.netCurrentAssets, element.assetId, element.company) | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="navMultipleBase">
                                    <th [ngClass]="getGroupCssClass('navMultipleBase')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('navMultipleBase')" [class.scenario-start]="isScenarioStart('navMultipleBase')">NAV Mult
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('navMultipleBase')"
                                        [class.scenario-start]="isScenarioStart('navMultipleBase')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.navMultipleBase | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="primaryTVPIBase">
                                    <th [ngClass]="getGroupCssClass('primaryTVPIBase')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('primaryTVPIBase')" [class.scenario-start]="isScenarioStart('primaryTVPIBase')">Prim. TVPI
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('primaryTVPIBase')"
                                        [class.scenario-start]="isScenarioStart('primaryTVPIBase')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.primaryTVPIBase | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="secondaryTVPIBase">
                                    <th [ngClass]="getGroupCssClass('secondaryTVPIBase')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('secondaryTVPIBase')" [class.scenario-start]="isScenarioStart('secondaryTVPIBase')">Sec. TVPI
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('secondaryTVPIBase')"
                                        [class.scenario-start]="isScenarioStart('secondaryTVPIBase')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.secondaryTVPIBase | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="secondaryIRRBase">
                                    <th [ngClass]="getGroupCssClass('secondaryIRRBase')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('secondaryIRRBase')" [class.scenario-start]="isScenarioStart('secondaryIRRBase')">Sec. IRR
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('secondaryIRRBase')"
                                        [class.scenario-start]="isScenarioStart('secondaryIRRBase')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.secondaryIRRBase | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="cashflowHigh">
                                    <th [ngClass]="getGroupCssClass('cashflowHigh')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('cashflowHigh')" [class.scenario-start]="isScenarioStart('cashflowHigh')">High Case
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('cashflowHigh')"
                                        [class.scenario-start]="isScenarioStart('cashflowHigh')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ getDisplayScenarioValue(element.cashflowHigh, partnershipInvestments.partnershipInvestment.netCurrentAssets, element.assetId, element.company) | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="navMultipleHigh">
                                    <th [ngClass]="getGroupCssClass('navMultipleHigh')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('navMultipleHigh')" [class.scenario-start]="isScenarioStart('navMultipleHigh')">NAV Mult
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('navMultipleHigh')"
                                        [class.scenario-start]="isScenarioStart('navMultipleHigh')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.navMultipleHigh | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="primaryTVPIHigh">
                                    <th [ngClass]="getGroupCssClass('primaryTVPIHigh')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('primaryTVPIHigh')" [class.scenario-start]="isScenarioStart('primaryTVPIHigh')">Prim. TVPI
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('primaryTVPIHigh')"
                                        [class.scenario-start]="isScenarioStart('primaryTVPIHigh')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.primaryTVPIHigh | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="secondaryTVPIHigh">
                                    <th [ngClass]="getGroupCssClass('secondaryTVPIHigh')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('secondaryTVPIHigh')" [class.scenario-start]="isScenarioStart('secondaryTVPIHigh')">Sec. TVPI
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('secondaryTVPIHigh')"
                                        [class.scenario-start]="isScenarioStart('secondaryTVPIHigh')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.secondaryTVPIHigh | traceableFormat }}
                                    </td>
                                </ng-container>

                                <ng-container matColumnDef="secondaryIRRHigh">
                                    <th [ngClass]="getGroupCssClass('secondaryIRRHigh')" *matHeaderCellDef mat-header-cell mat-sort-header
                                        [class.grp-start-data]="groupStartCols.has('secondaryIRRHigh')" [class.scenario-start]="isScenarioStart('secondaryIRRHigh')">Sec. IRR
                                    </th>
                                    <td *matCellDef="let element" mat-cell
                                        [class.grp-start-data]="groupStartCols.has('secondaryIRRHigh')"
                                        [class.scenario-start]="isScenarioStart('secondaryIRRHigh')"
                                        (click)="openDialog(element, fundId, fundReportId, assetDialogExpandType.VALUATION)">
                                        {{ element.secondaryIRRHigh | traceableFormat }}
                                    </td>
                                </ng-container>

                                <!-- ==================== Empty footer cells (generated via @for) ==================== -->

                                @for (cellId of emptyFooterCells; track cellId) {
                                    <ng-container [matColumnDef]="cellId">
                                        <td mat-footer-cell *matFooterCellDef
                                            [class.grp-start-data]="isGroupStartFooterCell(cellId)"
                                            [class.scenario-start]="isScenarioStartFooterCell(cellId)"></td>
                                    </ng-container>
                                }

                                <!-- ==================== Data footer cells ==================== -->

                                <!-- Footer: company (sticky) -->
                                <ng-container matColumnDef="company__tua" sticky>
                                    <td mat-footer-cell *matFooterCellDef>{{ footerTUA?.company | traceableFormat }}</td>
                                </ng-container>
                                <ng-container matColumnDef="company__nca" sticky>
                                    <td mat-footer-cell *matFooterCellDef>{{ footerNCA?.company | traceableFormat }}</td>
                                </ng-container>
                                <ng-container matColumnDef="company__gpcr" sticky>
                                    <td mat-footer-cell *matFooterCellDef>{{ footerGPCR?.company | traceableFormat }}</td>
                                </ng-container>
                                <ng-container matColumnDef="company__bpa" sticky>
                                    <td mat-footer-cell *matFooterCellDef>{{ footerBPA?.company | traceableFormat }}</td>
                                </ng-container>
                                <ng-container matColumnDef="company__total" sticky>
                                    <td mat-footer-cell *matFooterCellDef>{{ footerTOTAL?.company | traceableFormat }}</td>
                                </ng-container>

                                <!-- Footer: remainingCost -->
                                <ng-container matColumnDef="remainingCost__tua">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('remainingCost')">{{ footerTUA?.remainingCost | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="remainingCost__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('remainingCost')">{{ footerTOTAL?.remainingCost | traceableFormat }}
                                    </td>
                                </ng-container>

                                <!-- Footer: gpNAV -->
                                <ng-container matColumnDef="gpNAV__tua">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('gpNAV')">{{ footerTUA?.gpNAV | traceableFormat }}</td>
                                </ng-container>
                                <ng-container matColumnDef="gpNAV__nca">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('gpNAV')">{{ footerNCA?.gpNAV | traceableFormat }}</td>
                                </ng-container>
                                <ng-container matColumnDef="gpNAV__gpcr">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('gpNAV')">{{ footerGPCR?.gpNAV | traceableFormat }}</td>
                                </ng-container>
                                <ng-container matColumnDef="gpNAV__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('gpNAV')">{{ footerTOTAL?.gpNAV | traceableFormat }}</td>
                                </ng-container>

                                <!-- Footer: unrealizedTVPI -->
                                <ng-container matColumnDef="unrealizedTVPI__tua">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('unrealizedTVPI')">{{ footerTUA?.unrealizedTVPI | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="unrealizedTVPI__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('unrealizedTVPI')">{{ footerTOTAL?.unrealizedTVPI | traceableFormat }}
                                    </td>
                                </ng-container>

                                <!-- Footer: totalTVPI -->
                                <ng-container matColumnDef="totalTVPI__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('totalTVPI')">{{ footerTOTAL?.totalTVPI | traceableFormat }}
                                    </td>
                                </ng-container>

                                <!-- Footer: selectedBid -->
                                <ng-container matColumnDef="selectedBid__tua">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('selectedBid')">{{ footerTUA?.selectedBid | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="selectedBid__bpa">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('selectedBid')">{{ footerBPA?.selectedBid | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="selectedBid__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('selectedBid')">{{ footerTOTAL?.selectedBid | traceableFormat }}
                                    </td>
                                </ng-container>

                                <!-- Footer: discountToNAV -->
                                <ng-container matColumnDef="discountToNAV__tua">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('discountToNAV')">{{ footerTUA?.discountToNAV | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="discountToNAV__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('discountToNAV')">{{ footerTOTAL?.discountToNAV | traceableFormat }}
                                    </td>
                                </ng-container>

                                <!-- Footer: GP Report money columns (tua + total) -->
                                <ng-container matColumnDef="realizedCost__tua">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('realizedCost')">{{ footerTUA?.realizedCost | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="realizedCost__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('realizedCost')">{{ footerTOTAL?.realizedCost | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="realizedGains__tua">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('realizedGains')">{{ footerTUA?.realizedGains | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="realizedGains__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('realizedGains')">{{ footerTOTAL?.realizedGains | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="totalRealized__tua">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('totalRealized')">{{ footerTUA?.totalRealized | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="totalRealized__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('totalRealized')">{{ footerTOTAL?.totalRealized | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="totalCost__tua">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('totalCost')">{{ footerTUA?.totalCost | traceableFormat }}</td>
                                </ng-container>
                                <ng-container matColumnDef="totalCost__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('totalCost')">{{ footerTOTAL?.totalCost | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="mezzanine__tua">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('mezzanine')">{{ footerTUA?.mezzanine | traceableFormat }}</td>
                                </ng-container>
                                <ng-container matColumnDef="mezzanine__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('mezzanine')">{{ footerTOTAL?.mezzanine | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="preferredEquity__tua">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('preferredEquity')">{{ footerTUA?.preferredEquity | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="preferredEquity__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('preferredEquity')">{{ footerTOTAL?.preferredEquity | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="commonEquity__tua">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('commonEquity')">{{ footerTUA?.commonEquity | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="commonEquity__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('commonEquity')">{{ footerTOTAL?.commonEquity | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="committedInvestment__tua">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('committedInvestment')">{{ footerTUA?.committedInvestment | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="committedInvestment__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('committedInvestment')">{{ footerTOTAL?.committedInvestment | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="capexDrawdown__tua">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('capexDrawdown')">{{ footerTUA?.capexDrawdown | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="capexDrawdown__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('capexDrawdown')">{{ footerTOTAL?.capexDrawdown | traceableFormat }}
                                    </td>
                                </ng-container>

                                <!-- Footer: percentageOfTotalBid / percentageOfTotalNAV -->
                                <ng-container matColumnDef="percentageOfTotalBid__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('percentageOfTotalBid')">{{ footerTOTAL?.percentageOfTotalBid | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="percentageOfTotalNAV__total">
                                    <td mat-footer-cell *matFooterCellDef
                                        [class.grp-start-data]="groupStartCols.has('percentageOfTotalNAV')">{{ footerTOTAL?.percentageOfTotalNAV | traceableFormat }}
                                    </td>
                                </ng-container>

                                <!-- Footer: cashflow scenarios -->
                                <ng-container matColumnDef="cashflowLow__nca">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('cashflowLow')"
                                        [class.scenario-start]="isScenarioStart('cashflowLow')">{{ footerNCA?.cashflowLow | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="cashflowLow__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('cashflowLow')"
                                        [class.scenario-start]="isScenarioStart('cashflowLow')">{{ footerTOTAL?.cashflowLow | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="cashflowBase__nca">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('cashflowBase')"
                                        [class.scenario-start]="isScenarioStart('cashflowBase')">{{ footerNCA?.cashflowBase | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="cashflowBase__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('cashflowBase')"
                                        [class.scenario-start]="isScenarioStart('cashflowBase')">{{ footerTOTAL?.cashflowBase | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="cashflowHigh__nca">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('cashflowHigh')"
                                        [class.scenario-start]="isScenarioStart('cashflowHigh')">{{ footerNCA?.cashflowHigh | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="cashflowHigh__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('cashflowHigh')"
                                        [class.scenario-start]="isScenarioStart('cashflowHigh')">{{ footerTOTAL?.cashflowHigh | traceableFormat }}
                                    </td>
                                </ng-container>

                                <!-- Footer: scenario totals (navMultiple, primaryTVPI, secondaryTVPI) -->
                                <ng-container matColumnDef="navMultipleLow__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('navMultipleLow')"
                                        [class.scenario-start]="isScenarioStart('navMultipleLow')">{{ footerTOTAL?.navMultipleLow | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="primaryTVPILow__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('primaryTVPILow')"
                                        [class.scenario-start]="isScenarioStart('primaryTVPILow')">{{ footerTOTAL?.primaryTVPILow | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="secondaryTVPILow__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('secondaryTVPILow')"
                                        [class.scenario-start]="isScenarioStart('secondaryTVPILow')">{{ footerTOTAL?.secondaryTVPILow | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="navMultipleBase__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('navMultipleBase')"
                                        [class.scenario-start]="isScenarioStart('navMultipleBase')">{{ footerTOTAL?.navMultipleBase | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="primaryTVPIBase__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('primaryTVPIBase')"
                                        [class.scenario-start]="isScenarioStart('primaryTVPIBase')">{{ footerTOTAL?.primaryTVPIBase | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="secondaryTVPIBase__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('secondaryTVPIBase')"
                                        [class.scenario-start]="isScenarioStart('secondaryTVPIBase')">{{ footerTOTAL?.secondaryTVPIBase | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="navMultipleHigh__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('navMultipleHigh')"
                                        [class.scenario-start]="isScenarioStart('navMultipleHigh')">{{ footerTOTAL?.navMultipleHigh | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="primaryTVPIHigh__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('primaryTVPIHigh')"
                                        [class.scenario-start]="isScenarioStart('primaryTVPIHigh')">{{ footerTOTAL?.primaryTVPIHigh | traceableFormat }}
                                    </td>
                                </ng-container>
                                <ng-container matColumnDef="secondaryTVPIHigh__total">
                                    <td mat-footer-cell *matFooterCellDef [class.grp-start-data]="groupStartCols.has('secondaryTVPIHigh')"
                                        [class.scenario-start]="isScenarioStart('secondaryTVPIHigh')">{{ footerTOTAL?.secondaryTVPIHigh | traceableFormat }}
                                    </td>
                                </ng-container>

                                <!-- ==================== Group header column definitions ==================== -->

                                @for (col of allGroupableCols; track col) {
                                    <ng-container [matColumnDef]="col + '__grp'" [sticky]="col === 'company'">
                                        <th mat-header-cell *matHeaderCellDef
                                            [class.grp-start]="isGroupStart(col)"
                                            [ngClass]="getGroupCssClass(col)">
                                            @if (isGroupLabel(col)) {
                                                <span class="grp-label">{{ getGroupLabel(col) }}</span>
                                            }
                                        </th>
                                    </ng-container>
                                }

                                <!-- ==================== Row definitions ==================== -->

                                @let displayedColumns = (displayedColumns$ | async) ?? [];
                                <tr mat-header-row *matHeaderRowDef="groupHeaderColIds; sticky: true" class="col-group-header-row"></tr>
                                <tr *matHeaderRowDef="displayedColumns; sticky: true" mat-header-row></tr>
                                <tr *matRowDef=" let row; columns: displayedColumns;"
                                    [ngClass]="styleRowExited(row)"
                                    mat-row>
                                </tr>

                                <tr mat-footer-row *matFooterRowDef="footerColsTUA" class="footer-row row-top-border not-clickable"></tr>
                                <tr mat-footer-row *matFooterRowDef="footerColsNCA" class="footer-row row-top-border not-clickable"></tr>
                                <tr mat-footer-row *matFooterRowDef="footerColsGPCR" class="footer-row not-clickable"></tr>
                                <tr mat-footer-row *matFooterRowDef="footerColsBPA" class="footer-row not-clickable"></tr>
                                <tr mat-footer-row *matFooterRowDef="footerColsTOTAL" class="row-important not-clickable"></tr>
                            </table>
                        </section>
                        <mat-expansion-panel [expanded]="panelOpenState" class="mat-expansion-panel">
                            <mat-expansion-panel-header class="mat-expansion-panel-header">
                                <mat-card-title data-test="title">Current and Realized Investments</mat-card-title>
                            </mat-expansion-panel-header>
                            <section class="table-container" tabindex="0">
                                <table [dataSource]="partnershipInvestments.performanceTotals" mat-table>
                                    <ng-container matColumnDef="company" sticky>
                                        <th *matHeaderCellDef mat-header-cell></th>
                                        <td *matCellDef="let element" class="no-wrap" mat-cell>{{ element.company | traceableFormat }}</td>
                                        <td *matFooterCellDef
                                            mat-footer-cell>{{ partnershipInvestments.performanceTotalCurrentAndRealizedInvestments.company | traceableFormat }}
                                        </td>
                                    </ng-container>
                                    <ng-container matColumnDef="realizedCost">
                                        <th *matHeaderCellDef mat-header-cell>Realized Cost</th>
                                        <td *matCellDef="let element" mat-cell>{{ element.realizedCost | traceableFormat }}</td>
                                        <td *matFooterCellDef
                                            mat-footer-cell>{{ partnershipInvestments.performanceTotalCurrentAndRealizedInvestments.realizedCost | traceableFormat }}
                                        </td>
                                    </ng-container>
                                    <ng-container matColumnDef="realizedGains">
                                        <th *matHeaderCellDef mat-header-cell>Realized Gains</th>
                                        <td *matCellDef="let element" mat-cell>{{ element.realizedGains | traceableFormat }}</td>
                                        <td *matFooterCellDef
                                            mat-footer-cell>{{ partnershipInvestments.performanceTotalCurrentAndRealizedInvestments.realizedGains | traceableFormat }}
                                        </td>
                                    </ng-container>
                                    <ng-container matColumnDef="totalCost">
                                        <th *matHeaderCellDef mat-header-cell>Total Cost</th>
                                        <td *matCellDef="let element" mat-cell>{{ element.totalCost | traceableFormat }}</td>
                                        <td *matFooterCellDef
                                            mat-footer-cell>{{ partnershipInvestments.performanceTotalCurrentAndRealizedInvestments.totalCost | traceableFormat }}
                                        </td>
                                    </ng-container>
                                    <ng-container matColumnDef="totalRealized">
                                        <th *matHeaderCellDef mat-header-cell>Total Realized</th>
                                        <td *matCellDef="let element" mat-cell>{{ element.totalRealized | traceableFormat }}</td>
                                        <td *matFooterCellDef
                                            mat-footer-cell>{{ partnershipInvestments.performanceTotalCurrentAndRealizedInvestments.totalRealized | traceableFormat }}
                                        </td>
                                    </ng-container>
                                    <tr *matHeaderRowDef="partnershipInvestments.performanceTotalColumns" mat-header-row></tr>
                                    <tr *matRowDef=" let row; columns: partnershipInvestments.performanceTotalColumns" class="not-clickable" mat-row></tr>
                                    <tr *matFooterRowDef="partnershipInvestments.performanceTotalColumns" class="row-important row-top-border" mat-footer-row></tr>
                                </table>
                            </section>
                            <valumize-fund-current-and-realized-investments
                                [columnDefinition]="partnershipInvestments.currentAndRealizedInvestmentColumns"
                                [dataSource]="partnershipInvestments.currentInvestments"
                                [fundId]="fundId"
                                [fundReportId]="fundReportId"
                                [fundValuationId]="fundValuationId"
                                [title]="'Current Investments'">
                            </valumize-fund-current-and-realized-investments>
                            <valumize-fund-current-and-realized-investments
                                [columnDefinition]="partnershipInvestments.currentAndRealizedInvestmentColumns"
                                [dataSource]="partnershipInvestments.realizedInvestments"
                                [fundId]="fundId"
                                [fundReportId]="fundReportId"
                                [fundValuationId]="fundValuationId"
                                [title]="'Realized Investments'">
                            </valumize-fund-current-and-realized-investments>
                        </mat-expansion-panel>
                    }
                </mat-card-content>
                <mat-card-actions class="detail-card-actions pi-actions">
                    <button (click)="openManageFundInvestmentsDialog(fundId, fundReportId)"
                            [disabled]="(isEditDisabled$ | async)!"
                            color="primary"
                            data-test="manage-fund-investments"
                            mat-stroked-button>
                        <mat-icon>settings</mat-icon>
                        Manage Investments
                    </button>
                    <button mat-icon-button
                            (click)="openTableSettings()"
                            matTooltip="Table Settings"
                            data-test="open-table-settings">
                        <mat-icon>view_column</mat-icon>
                    </button>
                    <valumize-card-comments-button [commentScope]="'fund-report'" [commentSlug]="'fund-partnership-investments'"></valumize-card-comments-button>
                </mat-card-actions>
            }
        } @else {
            <mat-card-content class="detail-card-content empty-state-message">
                Please select fund report first!
            </mat-card-content>
        }
    }
</mat-card>
